/**
 * Top-down layout for a v2 agent graph.
 *
 * Positions are computed every time the graph is drawn and are never part of
 * the definition. The canvas is not a drawing surface: nodes sit where the
 * graph puts them, so two people opening the same agent see the same picture
 * and nobody has to tidy anything.
 *
 * Layered: rank by longest path from the trigger, order each rank by the
 * average position of its parents so branches stay under what they branch
 * from and edges cross as little as possible, then centre each rank.
 *
 * Written in-repo rather than pulled from a layout library: the graphs are
 * tens of nodes and the rules are few.
 */

import { portsOf } from 'features/agents/WorkflowEditor/nodeCatalog';

export const NODE_W = 200;
export const NODE_H = 44;
export const V_GAP = 64;
export const H_GAP = 32;
/** Top-down. One constant so a left-to-right layout is a later, small change. */
export const LAYOUT_DIR = 'TB';
/**
 * A node whose config names its branches (Decide) prints each one under its
 * name along the bottom: a strip this tall, and room for each label.
 */
export const PORT_STRIP_H = 22;
export const PORT_SLOT_W = 96;

/** Labelled ports along the bottom: a node whose config names its branches. */
export function labelledPorts(node) {
  if (!node || node.kind !== 'decide') return null;
  const ports = portsOf(node) || [];
  return ports.length ? ports : null;
}

/**
 * The box a node occupies. Every node is NODE_W x NODE_H, except one that
 * prints its branches: it is as wide as its labels need and the strip taller.
 */
export function measureNode(node) {
  const ports = labelledPorts(node);
  if (!ports) return { w: NODE_W, h: NODE_H };
  return { w: Math.max(NODE_W, ports.length * PORT_SLOT_W), h: NODE_H + PORT_STRIP_H };
}

/**
 * @param {{nodes: Array<{id, kind}>, edges: Array<{from, to}>}} def
 * @param {(kind: string) => any} [specFor]  catalog lookup, for the trigger
 * @returns {Map<string, {x: number, y: number}>} position per node id
 */
export function layoutGraph(def, specFor = () => ({})) {
  const nodes = def?.nodes || [];
  const edges = def?.edges || [];
  const out = new Map();
  if (!nodes.length) return out;

  const ids = new Set(nodes.map((n) => n.id));
  const parents = new Map(nodes.map((n) => [n.id, []]));
  for (const e of edges) {
    if (!ids.has(e.from) || !ids.has(e.to) || e.from === e.to) continue;
    parents.get(e.to).push(e.from);
  }

  // Rank: longest path from a root. A cycle (which validation refuses but
  // the editor may briefly hold) is broken by the visiting guard.
  const rank = new Map();
  const visiting = new Set();
  const rankOf = (id) => {
    if (rank.has(id)) return rank.get(id);
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const ps = parents.get(id) || [];
    const r = ps.length ? Math.max(...ps.map((p) => rankOf(p) + 1)) : 0;
    visiting.delete(id);
    rank.set(id, r);
    return r;
  };
  for (const n of nodes) rankOf(n.id);
  for (const n of nodes) if ((specFor(n.kind) || {}).category === 'trigger') rank.set(n.id, 0);

  const ranks = [];
  for (const n of nodes) {
    const r = rank.get(n.id) || 0;
    (ranks[r] = ranks[r] || []).push(n);
  }

  // Which branch of its parent a node hangs from, in the parent's port order:
  // If's "true" sits left of "false"; a Decide's routes left to right as
  // listed, Otherwise last.
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const portRank = new Map();
  for (const e of edges) {
    if (!ids.has(e.from) || !ids.has(e.to) || e.from === e.to) continue;
    const ports = portsOf(byId.get(e.from)) || [];
    const idx = e.port == null ? -1 : ports.indexOf(e.port);
    const pr = idx < 0 ? 0 : idx;
    portRank.set(e.to, Math.min(portRank.get(e.to) ?? Number.MAX_SAFE_INTEGER, pr));
  }
  const size = new Map(nodes.map((n) => [n.id, measureNode(n)]));
  const widthOf = (id) => size.get(id).w;

  // Order within a rank by the parents' average x (barycentre), then put each
  // node as close under its parents as the row allows: a chain stays a
  // straight line and a branch's children sit under the branch.
  const sep = (a, b) => (widthOf(a.id) + widthOf(b.id)) / 2 + H_GAP;
  const placedX = new Map(); // centre x
  ranks.forEach((row, r) => {
    if (!row) return;
    const want = new Map();
    for (const n of row) {
      const ps = (parents.get(n.id) || []).filter((p) => placedX.has(p));
      want.set(n.id, ps.length ? ps.reduce((s, p) => s + placedX.get(p), 0) / ps.length : null);
    }
    if (r > 0) {
      row.sort((a, b) => {
        const wa = want.get(a.id); const wb = want.get(b.id);
        const ka = wa == null ? Number.MAX_SAFE_INTEGER : wa;
        const kb = wb == null ? Number.MAX_SAFE_INTEGER : wb;
        return (ka - kb) || ((portRank.get(a.id) ?? 0) - (portRank.get(b.id) ?? 0)) || String(a.id).localeCompare(String(b.id));
      });
    }
    // Nodes with no placed parent (a second root) go where the row leaves room.
    const xs = [];
    row.forEach((n, i) => {
      const w = want.get(n.id);
      const floor = i ? xs[i - 1] + sep(row[i - 1], n) : null;
      if (w == null) xs.push(floor ?? 0);
      else xs.push(floor == null ? w : Math.max(w, floor));
    });
    // The pack pushes right; recentre the row on where its nodes wanted to be.
    let shift = 0;
    let counted = 0;
    row.forEach((n, i) => { const w = want.get(n.id); if (w != null) { shift += w - xs[i]; counted += 1; } });
    shift = counted ? shift / counted : -((xs[xs.length - 1] - xs[0]) / 2);
    row.forEach((n, i) => placedX.set(n.id, xs[i] + shift));
  });

  // Each rank starts below the tallest node of the rank above.
  let minX = Infinity;
  let y = 0;
  ranks.forEach((row) => {
    if (!row) return;
    let tallest = NODE_H;
    for (const n of row) {
      const x = placedX.get(n.id) - widthOf(n.id) / 2;
      minX = Math.min(minX, x);
      out.set(n.id, { x, y });
      tallest = Math.max(tallest, size.get(n.id).h);
    }
    y += tallest + V_GAP;
  });
  // Into positive space, with a margin.
  const dx = 40 - (Number.isFinite(minX) ? minX : 0);
  for (const [id, p] of out) out.set(id, { x: Math.round(p.x + dx), y: Math.round(p.y + 40) });
  return out;
}
